import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  BrowserRouter, Route, Routes, Navigate,
} from 'react-router-dom';
import { useFetch } from './api';
import Connections from './Connections';
import Events from './Events';
import Webhooks from './Webhooks';
import Navbar from './Navbar';
import Homepage from './Homepage';
import Settings from './Settings';
import ApiLogPage from './ApiLogPage';
import Users from './Users';
import ApiKeys from './ApiKeys';
import ConnectionSettings from './ConnectionSettings';
import Branding from './Branding';
import MagicLinks from './MagicLinks';
import Notifications from './Notifications';
import UsageBilling from './UsageBilling';
import Integrations from './Integrations';
import Actions, { IntegrationDetail } from './IntegrationActions';
import DataExplorerPage from './DataExplorerPage';
import CommandPalette from './CommandPalette';

if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Orgs in these billing statuses see an uncloseable modal over the content
// area — the dashboard keeps loading real data behind the blur so customers
// see what they'd be giving up. Pokedex blocks their public API server-side
// but must keep internal/* endpoints open for this to show real data.
const blockedStatusScreens = {
  Inactive: {
    title: 'Your free trial has ended',
    body: 'Please reach out to support@trackstarhq.com to keep using Trackstar',
  },
  Churn: {
    title: 'Your account is deactivated',
    body: 'Please reach out to support@trackstarhq.com to reactivate your account',
  },
};

function App() {
  return (
    <BrowserRouter>
      <ClerkProvider
        publishableKey={clerkPubKey}
      >
        <ClerkProviderWithRoutes />
      </ClerkProvider>
    </BrowserRouter>
  );
}

function ClerkProviderWithRoutes() {
  const [numIssues, setNumIssues] = useState(0);
  const { userMemberships, userInvitations, setActive } = useOrganizationList({
    userMemberships: true,
    userInvitations: true,
  });
  const { organization } = useOrganization();
  const [connectionData, setConnectionData] = useState(null);
  const [loadingConnectionData, setLoadingConnectionData] = useState(false);
  // true only after every page loaded successfully; a partial list from a failed
  // mid-load must not read as the full picture on the homepage
  const [connectionsComplete, setConnectionsComplete] = useState(false);
  // set when a page request fails, so the homepage can stop loading and offer a
  // retry instead of spinning forever on a partial or empty list
  const [connectionsError, setConnectionsError] = useState(false);
  const [integrationLists, setIntegrationLists] = useState(null);
  const [integrationListsLoading, setIntegrationListsLoading] = useState(false);
  const [requestPathToReadableTitleLookup, setRequestPathToReadableTitleLookup] = useState({});
  const [openApiSpec, setOpenApiSpec] = useState(null);
  const [loadingOpenApiSpec, setLoadingOpenApiSpec] = useState(false);
  // undefined = still fetching (first paint waits on it so the trial chip
  // doesn't pop in and shift the layout); null = fetch failed, render without
  // billing UI — pokedex still enforces billing server-side
  const [orgInfo, setOrgInfo] = useState(undefined);
  const trackstar = useFetch();

  const fetchConnectionData = () => {
    setLoadingConnectionData(true);
    setConnectionsComplete(false);
    setConnectionsError(false);
    // the endpoint is paginated; follow next_token so orgs with many
    // connections see all of them, not just the first page
    const fetchAllPages = async () => {
      const allConnections = [];
      let pageToken = null;
      do {
        let path = 'internal/connections';
        if (pageToken) {
          path += `?page_token=${encodeURIComponent(pageToken)}`;
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await trackstar(path);
        allConnections.push(...response.data);
        // publish each page as it arrives so the homepage can render before the
        // last page loads; a fresh array so react picks up the change
        setConnectionData([...allConnections]);
        pageToken = response.next_token;
      } while (pageToken);
      return allConnections;
    };
    // returned so callers can await the refetch and avoid rendering stale rows
    return fetchAllPages().then((data) => {
      setLoadingConnectionData(false);
      setConnectionsComplete(true);
      let connectionIssues = 0;
      data.forEach((connection) => {
        connectionIssues += connection.errors.length;
      });
      setNumIssues(connectionIssues);
    }).catch((error) => {
      // a failed page must still clear the spinner instead of hanging the table
      console.error('Failed to load connections', error);
      setLoadingConnectionData(false);
      setConnectionsError(true);
    });
  };

  const integrationTypes = ['wms', 'cart', 'freight', 'carrier', 'erp'];
  const rawIntegrationLists = {};
  integrationTypes.forEach((integrationType) => {
    rawIntegrationLists[integrationType] = {};
  });
  const fetchIntegrationLists = () => {
    setIntegrationListsLoading(true);
    const getAllLists = async () => {
      const promises = integrationTypes.map(async (integrationType) => {
        const body = {
          endpoints: [],
          integration_allow_list: [],
          integration_block_list: [],
          allow_sandbox: true,
          integration_type: integrationType,
        };
        const data = await trackstar('internal/link/get-available-integrations', 'POST', body);
        const buildIntegrationList = {};
        Object.keys(data).forEach((integrationName) => {
          const displayName = data[integrationName].display_name;
          const dt = data[integrationName];
          dt.display_name = displayName;
          buildIntegrationList[integrationName] = dt;
        });
        rawIntegrationLists[integrationType] = buildIntegrationList;
      });
      await Promise.all(promises);
    };
    getAllLists().then(() => {
      setIntegrationLists(rawIntegrationLists);
      setIntegrationListsLoading(false);
    });
  };

  const fetchOpenApiSpec = async () => {
    setLoadingOpenApiSpec(true);
    const spec = await trackstar('openapi.json', 'GET');
    setOpenApiSpec(spec);
    const pathsWithPathParams = (p) => p.replace(/\{[^}]+\}/g, '<path_param>');
    const pathsWithIds = (p) => p.replace(/\{[^}]+\}/g, '<id>');
    const { paths } = spec;
    const lookup = new Map();
    // eslint-disable-next-line no-restricted-syntax
    for (const [rawPath, methods] of Object.entries(paths ?? {})) {
      // for integrations paths, replace all path params with <path_param>,
      // otherwise replace all path params with <id>
      const pathWithPathParams = rawPath.includes('integrations') ? pathsWithPathParams(rawPath) : pathsWithIds(rawPath);
      const methodMap = lookup.get(pathWithPathParams) ?? new Map();
      // eslint-disable-next-line no-restricted-syntax
      for (const [method, op] of Object.entries(methods ?? {})) {
        methodMap.set(method.toUpperCase(), op?.summary ?? '');
      }
      lookup.set(pathWithPathParams, methodMap);
    }
    setRequestPathToReadableTitleLookup(lookup);
    setLoadingOpenApiSpec(false);
  };

  // Clerk only activates an organization when a session is CREATED. Two ways
  // a signed-in user ends up org-less on the "organization is not set up"
  // screen, both hit by customers:
  //  1. They have a membership, but their session was created before the
  //     membership existed — activate their first membership.
  //  2. They clicked an invite email while already signed in — Clerk's hosted
  //     page drops the ticket without consuming it, leaving the invitation
  //     pending forever — accept it, then activate that org.
  // Guarded to fire once: setActive briefly puts useOrganization() back into a
  // loading state, and the list results are fresh objects each render, so an
  // unguarded effect would re-fire every render and spin forever.
  const hasSetActiveOrg = useRef(false);
  useEffect(() => {
    if (hasSetActiveOrg.current || organization) return;
    if (userMemberships?.isLoading || userInvitations?.isLoading) return;
    if (userMemberships?.data?.length > 0) {
      hasSetActiveOrg.current = true;
      setActive({ organization: userMemberships.data[0].organization.id });
    } else if (userInvitations?.data?.length > 0) {
      hasSetActiveOrg.current = true;
      const invitation = userInvitations.data[0];
      invitation.accept().then(() => {
        setActive({ organization: invitation.publicOrganizationData.id });
      });
    }
  }, [organization, userMemberships, userInvitations, setActive]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (!organization || hasFetched.current) return;
    hasFetched.current = true;
    trackstar('internal/org')
      .then((response) => {
        // fetch() resolves on 4xx/5xx too — normalize a missing body to null
        // so the first-paint gate doesn't spin forever
        setOrgInfo(response?.data ?? null);
      })
      .catch(() => {
        setOrgInfo(null);
      });
    Promise.all([
      fetchConnectionData(),
      fetchIntegrationLists(),
      fetchOpenApiSpec(),
    ]);
  }, [organization]);

  // Undefined means we are still loading the organization
  // Null means the organization does not exist
  if (organization === undefined || (organization && orgInfo === undefined)) {
    return <ClipLoader />;
  }

  const blockScreen = organization ? blockedStatusScreens[orgInfo?.billing_status] : null;
  return (
    <>
      <SignedIn>
        {blockScreen && (
          // starts at the sidebar's right edge (240px, matching Navbar's fixed
          // width) so navigation stays usable; z-index clears MUI's layers
          <div className="fixed inset-y-0 right-0 left-[240px] z-[1400] flex items-center justify-center backdrop-blur-[6px] bg-white/30">
            <div className="bg-white border border-surface-200 rounded-2xl shadow-xl p-10 mx-4 max-w-md text-center">
              <h1 className="text-2xl font-semibold text-surface-900">{blockScreen.title}</h1>
              <p className="text-base text-surface-600 pt-4">{blockScreen.body}</p>
            </div>
          </div>
        )}
        <div className="flex gap-3 bg-[#F3F3EF]">
          <Navbar orgInfo={orgInfo} />
          <CommandPalette connections={connectionData} integrationLists={integrationLists} />
          {organization === null
            ? (
              <div className="flex justify-center items-center h-screen w-full">
                <div className="flex flex-col justify-center items-center border p-10">
                  <h1 className="text-4xl font-semibold leading-9">Welcome to Trackstar</h1>
                  <p className="text-lg font-medium py-5">Your organization is not set up, please reach out to support@trackstarhq.com for access</p>
                </div>
              </div>
            )
            : (
              <Routes>
                <Route path="/" element={<Homepage connectionData={connectionData} connectionErrors={numIssues} loadingConnectionData={loadingConnectionData} connectionsComplete={connectionsComplete} connectionsError={connectionsError} retryConnections={fetchConnectionData} integrationLists={integrationLists} integrationListsLoading={integrationListsLoading} orgInfo={orgInfo} />} />
                <Route
                  path="/connections"
                  element={
                    (
                      <Connections
                        fetchConnectionData={fetchConnectionData}
                        connectionIssues={numIssues}
                        connectionData={connectionData}
                        loadingConnectionData={loadingConnectionData}
                        integrationLists={integrationLists}
                        integrationListsLoading={integrationListsLoading}
                      />
                    )
                  }
                />
                <Route
                  path="/events"
                  element={(
                    <Events
                      integrationLists={integrationLists}
                      connectionData={connectionData}
                    />
                  )}
                />
                <Route path="/webhooks" element={<Webhooks />} />
                <Route
                  path="/settings"
                  element={(
                    <Settings
                      connectionData={connectionData}
                      integrationLists={integrationLists}
                      integrationListsLoading={integrationListsLoading}
                    />
                  )}
                >
                  <Route index element={<Navigate replace to="users" />} />
                  <Route path="users" element={<Users />} />
                  <Route path="api-keys" element={<ApiKeys />} />
                  <Route path="connection-settings" element={<ConnectionSettings />} />
                  <Route path="branding" element={<Branding />} />
                  <Route path="magic-links" element={<MagicLinks />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="usage-billing" element={<UsageBilling />} />
                </Route>
                <Route
                  path="/api-log"
                  element={
                    (
                      <ApiLogPage
                        connectionData={connectionData}
                        loadingConnectionData={loadingConnectionData}
                        integrationLists={integrationLists}
                        integrationListsLoading={integrationListsLoading}
                        requestPathToReadableTitleLookup={requestPathToReadableTitleLookup}
                        loadingOpenApiSpec={loadingOpenApiSpec}
                      />
                    )
                  }
                />
                <Route path="/sandbox" element={<Navigate replace to="/data-explorer" />} />
                <Route
                  path="/data-explorer"
                  element={(
                    <DataExplorerPage
                      organization={organization}
                      connectionData={connectionData}
                      fetchConnectionData={fetchConnectionData}
                      openApiSpec={openApiSpec}
                    />
                  )}
                />
                <Route path="/integrations" element={<Integrations />}>
                  <Route index element={<Actions />} />
                  <Route path="actions" element={<Navigate replace to="/integrations" />} />
                  <Route path=":type/:name" element={<IntegrationDetail />} />
                </Route>
              </Routes>
            )}
        </div>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

export default App;
